import React, { createContext, ReactNode, useContext, useEffect } from "react";
import { IPrivateWallets } from "../WithdrawAmount/IWithdrawAmount";
import FundsService from "@services/Funds.service";
import {
  getWalletFee,
  getWalletFeeObject,
} from "@funds/features/helpers/validation";
import { withRouter, RouteComponentProps } from "react-router";
import { catchError } from "@features/utils/functions";
import { IAddBankAccount, IWithdrawFees } from "../WithdrawForm/IWithdrawForm";
import { mainCurrencies } from "@bundles/consts";

export enum WithdrawalProcessSteps {
  SELECT_METHOD = 1,
  WITHDRAWAL_CRYPTO = 2,
  WITHDRAWAL_SUMMARY = 3,
  WITHDRAWAL_VERIFICATION = 4,
  WITHDRAWAL_BANK_TRANSFER = 5,
  MFA_CODE = 6,
  WALLET_FORM = 7,
  ADD_WALLET = 8,
  CONFIRM_EMAIL = 9,
}

interface WithdrawalProcessContextProps {
  isLoading: boolean;
  selectedMethod: "crypto" | "bankTransfer" | undefined;
  step: WithdrawalProcessSteps;
  selectedCryptoWallet: IPrivateWallets | IAddBankAccount | undefined;
  walletFeeObject: IWithdrawFees | undefined;
  currency: string | undefined;
  amount: string;
  percentage_value: string | undefined;
}

type ProviderProps = {
  state: WithdrawalProcessContextProps;
  setState: React.Dispatch<React.SetStateAction<WithdrawalProcessContextProps>>;
};

const WithdrawalProcessCtx = createContext<ProviderProps | undefined>(
  undefined
);

type Props = {
  children: ReactNode;
} & RouteComponentProps<{ currency: string }>;

export const WithdrawalProcess = withRouter(
  ({ children, match, location, currency }: Props & any) => {
    const isMainCurrency = mainCurrencies.includes(currency);

    const [state, setState] = React.useState<WithdrawalProcessContextProps>({
      isLoading: true,
      selectedMethod: !isMainCurrency ? "crypto" : undefined,
      step: !isMainCurrency
        ? WithdrawalProcessSteps.WITHDRAWAL_CRYPTO
        : WithdrawalProcessSteps.SELECT_METHOD,
      selectedCryptoWallet: undefined,
      walletFeeObject: undefined,
      currency: undefined,
      amount: "",
      percentage_value: undefined,
    });

    useEffect(() => {
      FundsService.getWithdrawFees()
        .then((res) => {
          const fees = res.data;

          const query = new URLSearchParams(location.search);

          const currency = query.get("currency") ?? undefined;
          const walletFeeObject = getWalletFeeObject(fees, currency);

          setState({
            ...state,
            isLoading: false,
            walletFeeObject,
            currency,
          });
        })
        .catch((err) => catchError(err, true));
    }, []);

    return (
      <WithdrawalProcessCtx.Provider value={{ state, setState }}>
        {children}
      </WithdrawalProcessCtx.Provider>
    );
  }
);

export const useWithdrawalProcess = (): ProviderProps => {
  const ctxValue = useContext(WithdrawalProcessCtx);
  if (!ctxValue) {
    throw new Error("WithdrawalProcess - Expected context value to be set");
  }
  return ctxValue;
};

export default WithdrawalProcess;
